class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    /* Guard: a <predictive-search> with no inner search input leaves this.input
       null, and reading this.input.form then throws "null (reading 'form')",
       killing the whole script. ES5-safe checks only (no optional chaining) so
       this stays parseable on the old WebKit the FB/IG in-app browser ships. */
    if (this.input && this.input.form) {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    if (this.input) {
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    // Suppress the native submit when an option is selected — whether the
    // selected element IS the link/button (banner, "View all", quote-escape) or
    // CONTAINS one (li rows). Mirrors selectOption()'s matching so keyboard
    // Enter follows the option's own destination instead of going to /search.
    const sel = this.querySelector('[aria-selected="true"]');
    if (!this.getQuery().length || (sel && (sel.matches('a, button') || sel.querySelector('a, button')))) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close(false);
        this.input.focus();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      var escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var matches = currentButtonText.match(new RegExp(escapedTerm, 'g'));
      if (matches && matches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    // Keyboard-navigable options: result rows (li), the curated collection
    // banner (a.ccs-ps-banner), and the footer actions (.ccs-ps-search-for —
    // both the "View all" button and the zero-result quote-escape anchor).
    const allVisibleElements = Array.from(this.querySelectorAll('li, a.ccs-ps-banner, .ccs-ps-search-for')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    // Works whether the selected option IS the link (banner anchor, search-for
    // button) or CONTAINS one (li row → inner <a>).
    const selected = this.querySelector('[aria-selected="true"]');
    if (!selected) return;
    const link = selected.matches('a, button') ? selected : selected.querySelector('a, button');
    if (link) link.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,query,collection&resources[limit]=6&resources[options][fields]=title,product_type,vendor,tag`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.textContent = '';
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.highlightSearchTerm(this.searchTerm);

    // Notify the search-analytics listener (GA4/Clarity: search, zero-result).
    try {
      document.dispatchEvent(new CustomEvent('ccs:predictive-rendered', {
        detail: { root: this.predictiveSearchResults, term: this.searchTerm },
      }));
    } catch (_e) {}
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const anchor = this.input || this.querySelector('.ccs-mm-d-search') || this.querySelector('.ccs-mm-m-search');
    this.resultsMaxHeight = anchor
      ? `${window.innerHeight - anchor.getBoundingClientRect().bottom - 16}px`
      : `${window.innerHeight * 0.6}px`;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  highlightSearchTerm(searchTerm) {
    // Amazon-style highlighting:
    //   - Mark only WORD-BOUNDARY occurrences of the term (so "s" highlights
    //     "Scrubbers" + "Scouring" but NOT the trailing "s" in "Scrubbers").
    //   - If the term doesn't appear at any word start, fall back to the FIRST
    //     occurrence anywhere (so "ub" in "Scrubbers" still gets one mark).
    if (!searchTerm) return;
    // Only product + collection titles — suggestion rows render Shopify's own
    // styled_text highlighting, which textContent rebuild would otherwise wipe.
    var titles = this.predictiveSearchResults.querySelectorAll('.ccs-ps-row-product .ccs-ps-title, .ccs-ps-row-collection .ccs-ps-title');
    var escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var wordBoundaryRegex = new RegExp('\\b(' + escaped + ')', 'gi');
    var anywhereRegex = new RegExp('(' + escaped + ')', 'i');
    titles.forEach(function (title) {
      var text = title.textContent;
      var ranges = [];
      var match;
      while ((match = wordBoundaryRegex.exec(text)) !== null) {
        ranges.push({ start: match.index, end: match.index + match[1].length, text: match[1] });
      }
      if (ranges.length === 0) {
        match = anywhereRegex.exec(text);
        if (match) ranges.push({ start: match.index, end: match.index + match[1].length, text: match[1] });
      }
      if (ranges.length === 0) return;

      var fragment = document.createDocumentFragment();
      var lastEnd = 0;
      ranges.forEach(function (r) {
        if (r.start > lastEnd) fragment.appendChild(document.createTextNode(text.slice(lastEnd, r.start)));
        var mark = document.createElement('mark');
        mark.textContent = r.text;
        fragment.appendChild(mark);
        lastEnd = r.end;
      });
      if (lastEnd < text.length) fragment.appendChild(document.createTextNode(text.slice(lastEnd)));
      title.textContent = '';
      title.appendChild(fragment);
    });
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
